/**
 * Вкладка «Библиотека» «Голосового стенда» (`?tab=phrases`): готовые фразы робота по шагам сценария — где звучат,
 * кто играет (агент сам или по команде модели), запись голосом звонка, что услышал GigaAM и совпадение с текстом.
 * Название салона распознавание подтверждает плохо (редкое слово) — у фразы с ним дубли, лучший выбирают на слух.
 * Запись и перегенерация — `scripts.voice_lab.phrases` (`--again`, `--id`, `--takes N`). `GET /api/voice-lab/phrases`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useState } from "react";

import { type LabPhrase, labPhraseChoose, labPhraseStatsQuery, labPhraseUrl, labPhrasesQuery } from "@/lib/voiceLab";

const КТО: Record<LabPhrase["by"], string> = {
  agent: "играет агент сам, без модели",
  model: "по команде модели",
};

function Балл({ score, brand }: { score: number; brand: boolean | null }) {
  const хорошо = score >= 0.85 && brand !== false;
  return (
    <span className={`shrink-0 text-[11.5px] ${хорошо ? "text-ok" : "text-warn"}`}>
      совпадение {Math.round(score * 100)} %{brand === false ? " · название не распознано" : brand ? " · название ✓" : ""}
    </span>
  );
}

export function VoiceLabPhrases() {
  const voice = "marin";
  const q = useQuery(labPhrasesQuery(voice));
  const qc = useQueryClient();
  const choose = useMutation({
    mutationFn: ({ code, take }: { code: string; take: number }) => labPhraseChoose(code, take, voice),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voice-lab-phrases", voice] }),
  });
  const phrases = q.data?.phrases ?? [];
  const шаги = [...new Set(phrases.map((p) => p.step))];
  return (
    <section className="flex max-w-4xl flex-col gap-3 text-[12.5px]" data-annot="библиотека фраз">
      <div className="text-[12px] text-fg-muted">
        Постоянные фразы робота записаны заранее голосом звонка ({voice}, gpt-realtime-2.1, скорость 1,05) и проверены GigaAM: в разговоре
        их не нужно озвучивать заново — дешевле и звучат одинаково. Пока это библиотека для прослушивания: в звонок фразы включаем после
        вашего одобрения. Перезаписать: <code>scripts.voice_lab.phrases --again</code>.
      </div>
      {q.isError && <div className="text-err">Библиотека не загрузилась</div>}
      {choose.isError && <div className="text-err">Не удалось выбрать дубль</div>}
      {шаги.map((шаг) => (
        <div key={шаг} className="flex flex-col">
          <div className="mb-1 mt-2 text-[13px] font-semibold">{шаг}</div>
          {phrases
            .filter((p) => p.step === шаг)
            .map((p) => (
              <div key={p.code} className="flex flex-col gap-1.5 border-b border-border py-2">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="font-medium">«{p.text}»</span>
                </div>
                <div className="text-[11.5px] text-fg-muted">
                  Когда: {p.when} · {КТО[p.by]} · <span className="font-mono">{p.code}</span>
                </div>
                {p.recording ? (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <audio
                        controls
                        preload="none"
                        src={labPhraseUrl(voice, p.recording.file, p.recording.chosen_at ?? p.recording.at)}
                        className="h-8 min-w-0 flex-1"
                      />
                      <Балл score={p.recording.score} brand={p.recording.brand_ok} />
                    </div>
                    <div className="text-[11.5px] text-fg-muted">
                      GigaAM: «{p.recording.heard}»
                      {p.recording.chosen_take ? ` · выбран дубль ${p.recording.chosen_take} на слух` : ""}
                    </div>
                  </>
                ) : (
                  <div className="text-fg-muted">Не записана — scripts.voice_lab.phrases --id {p.code}</div>
                )}
                {p.takes.length > 0 && (
                  <div className="ml-4 flex flex-col gap-1">
                    <div className="text-[11.5px] text-fg-muted">Дубли — выберите лучший на слух:</div>
                    {p.takes.map((t) => {
                      const выбран = p.recording?.chosen_take === t.take;
                      return (
                        <div key={t.file} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className={`w-16 shrink-0 ${выбран ? "font-semibold text-accent" : ""}`}>дубль {t.take}</span>
                          <audio controls preload="none" src={labPhraseUrl(voice, t.file, t.at)} className="h-7 min-w-0 flex-1" />
                          <button
                            type="button"
                            disabled={choose.isPending}
                            onClick={() => choose.mutate({ code: p.code, take: t.take })}
                            className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${выбран ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
                          >
                            {выбран ? "Выбран" : "Выбрать"}
                          </button>
                          <span className="w-full pl-[72px] text-[11px] text-fg-muted">GigaAM: «{t.heard}»</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>
      ))}
      {!q.isLoading && !phrases.length && <div className="text-fg-muted">Фраз нет</div>}
      <Частые />
    </section>
  );
}

/** Что робот говорит чаще всего (по журналу звонков): решаем, какие фразы дозаписать. */
function Частые() {
  const [days, setDays] = useState(30);
  const q = useQuery(labPhraseStatsQuery(days));
  const фразы = q.data?.phrases ?? [];
  return (
    <div className="mt-4 flex flex-col gap-1.5" data-annot="частые фразы робота">
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="text-[13px] font-semibold">Что робот говорит чаще всего</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`h-6 rounded-[var(--radius-ui)] border px-2 text-[11.5px] ${d === days ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
            >
              {d} дн.
            </button>
          ))}
        </div>
        {q.data && <span className="text-[11.5px] text-fg-muted">звонков с репликами робота: {q.data.calls}</span>}
      </div>
      <div className="text-[11.5px] text-fg-muted">
        Предложения робота из журнала звонков, одинаковые слиты (без регистра и знаков, числа — «#»). «Кандидат» — частая фраза без
        подстановок, которой ещё нет в библиотеке: её стоит записать. С числом (дата, время, сумма) готовой записью не закрыть.
      </div>
      {q.isError && <div className="text-err">Статистика не загрузилась</div>}
      <table className="text-[12px]">
        <thead>
          <tr className="text-left text-[11px] text-fg-muted">
            <th className="py-1 pr-3 font-normal">Звонков</th>
            <th className="py-1 pr-3 font-normal">Раз</th>
            <th className="py-1 pr-3 font-normal">Фраза</th>
            <th className="py-1 font-normal">Библиотека</th>
          </tr>
        </thead>
        <tbody>
          {фразы.slice(0, 40).map((x) => (
            <tr key={x.text} className="border-t border-border align-top">
              <td className="py-1 pr-3 tabular-nums">
                {x.calls} <span className="text-fg-muted">({Math.round(x.share * 100)} %)</span>
              </td>
              <td className="py-1 pr-3 tabular-nums">{x.n}</td>
              <td className="py-1 pr-3">{x.text}</td>
              <td className="py-1 whitespace-nowrap">
                {x.library ? (
                  <span className="text-ok">
                    {x.library}
                    {x.played ? ` · записью ${x.played}` : ""}
                  </span>
                ) : x.variable ? (
                  <span className="text-fg-muted">с числом</span>
                ) : (
                  <span className="text-warn">кандидат</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
